import type { ReimbursmentRequest } from "@ied/shared-types";
import {
	createColumnHelper,
	getCoreRowModel,
	type TableOptions,
} from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { useGetReimbursementsRequest } from "@/hooks/queries/use-get-reimbursement-requests";
import { Badge } from "../ui/badge";
import { TanstackTable } from "../ui/table";

export function ReimbursementsTable() {
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(20);

	const { data } = useGetReimbursementsRequest({
		page: pageIndex + 1, // PocketBase uses 1-based pages
		perPage: pageSize,
	});

	const handlePaginationChange = useCallback(
		(state: { pageIndex: number; pageSize: number }) => {
			setPageIndex(state.pageIndex);
			setPageSize(state.pageSize);
		},
		[],
	);

	if (!data) {
		return <p>No reimbursement requests</p>;
	}

	const cHelper = createColumnHelper<ReimbursmentRequest>();
	const columns = [
		cHelper.accessor("id", {
			header: () => "ID",
		}),
		cHelper.accessor("requester_name", {
			header: () => "Requester Name",
			cell: (props) => props.getValue() || "-",
		}),
		cHelper.accessor("requester_type", {
			header: () => "Requester Type",
			cell: (props) => {
				const type = props.getValue();
				return (
					<Badge variant={type === "external" ? "secondary" : "default"}>
						{type.charAt(0).toUpperCase() + type.slice(1)}
					</Badge>
				);
			},
		}),
		cHelper.accessor("amount", {
			header: () => "Amount",
			cell: (props) => {
				const amount = props.getValue();
				return new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
				}).format(amount);
			},
		}),
		cHelper.accessor("status", {
			header: () => "Status",
			cell: (props) => {
				const status = props.getValue();
				const getStatusVariant = (
					status: string,
				): "default" | "secondary" | "destructive" | "outline" => {
					switch (status) {
						case "COMPLETE":
							return "default";
						case "PENDING":
						case "REQUESTED":
							return "secondary";
						case "IN_REVIEW":
							return "outline";
						case "DENIED":
							return "destructive";
						case "CHANGES_REQUESTED":
							return "outline";
						default:
							return "outline";
					}
				};

				return (
					<Badge variant={getStatusVariant(status)}>
						{status.replace(/_/g, " ")}
					</Badge>
				);
			},
		}),
		cHelper.accessor("reviewer_name", {
			header: () => "Reviewer",
			cell: (props) => props.getValue() || "-",
		}),
		cHelper.accessor("requester_notes", {
			header: () => "Requester Notes",
			cell: (props) => {
				const notes = props.getValue();
				if (!notes) return "-";
				return notes.length > 50 ? `${notes.substring(0, 50)}...` : notes;
			},
		}),
		cHelper.accessor("created", {
			header: () => "Created",
			cell: (row) => (
				<div>
					<p>{new Date(row.getValue()).toLocaleDateString()}</p>
					<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
				</div>
			),
		}),
		cHelper.accessor("updated", {
			header: () => "Updated",
			cell: (row) => (
				<div>
					<p>{new Date(row.getValue()).toLocaleDateString()}</p>
					<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
				</div>
			),
		}),
	];

	const tableOptions: TableOptions<ReimbursmentRequest> = {
		columns,
		data: data.items,
		getCoreRowModel: getCoreRowModel(),
	};

	return (
		<>
			<TanstackTable
				options={tableOptions}
				paginationOptions={{
					isPaginated: true,
					pageIndex,
					pageSize,
					totalCount: data.totalItems,
					onPaginationChange: handlePaginationChange,
				}}
			/>
		</>
	);
}
